"use client"

import { useCallback, useEffect, useMemo, useRef } from "react"

import { useRouter } from "next/navigation"

import { asRoute } from "@/lib/routes"
import { prepareBrowseNavigation } from "@/platform/app-mode/browse-history"
import { storefrontNavigationUrl } from "@/platform/app-mode/navigation-path"
import { useIsAppNavigation } from "@/platform/app-mode/NavigationProvider"
import { hintable, proceedsInPage, visitActionFor } from "@/platform/app-mode/visit"
import { createVisitNegotiator } from "@/platform/app-mode/visit-negotiation"
import { useNativeBridge } from "@/platform/bridge/use-native-bridge"

import type { Route } from "next"

export interface AppModeRouter {
  readonly navigate: (href: string, options?: { replace?: boolean }) => void
  readonly hint: (href: string) => void
}

const relativeTo = (target: URL): Route => asRoute(`${target.pathname}${target.search}${target.hash}`)

export const useAppModeRouter = (): AppModeRouter => {
  const bridge = useNativeBridge()
  const router = useRouter()
  const isApp = useIsAppNavigation()
  const hinted = useRef("")
  const inFlight = useRef(new Map<string, number>())
  const latest = useRef(0)

  const negotiator = useMemo(() => createVisitNegotiator((url, action) => void bridge.send("visit", { url, action })), [bridge])

  useEffect(() => {
    if (!bridge.available) return

    const off = bridge.on("visit.allow", negotiator.settle)
    return () => {
      off()
      negotiator.dispose()
    }
  }, [bridge, negotiator])

  const navigate = useCallback(
    (href: string, options?: { replace?: boolean }) => {
      const target = storefrontNavigationUrl(href, window.location.href, isApp)

      if (!bridge.available) {
        if (options?.replace) router.replace(relativeTo(target))
        else router.push(relativeTo(target))
        return
      }

      const action = options?.replace ? "replace" : visitActionFor(window.location.href, target.href)
      const key = `${action}:${target.href}`
      const sequence = ++latest.current
      const proposed = inFlight.current.has(key)
      inFlight.current.set(key, sequence)
      if (proposed) return

      void negotiator
        .propose(target.href, action)
        .then((outcome) => {
          if (inFlight.current.get(key) !== latest.current || !proceedsInPage(outcome, target.href)) return
          prepareBrowseNavigation()
          if (action === "replace") router.replace(relativeTo(target))
          else router.push(relativeTo(target))
        })
        .finally(() => inFlight.current.delete(key))
    },
    [bridge, negotiator, router, isApp]
  )

  const hint = useCallback(
    (href: string) => {
      if (!bridge.available) return

      const target = storefrontNavigationUrl(href, window.location.href, isApp).href
      if (target === hinted.current || !hintable(target)) return

      hinted.current = target
      router.prefetch(relativeTo(new URL(target)))
      bridge.send("visit.hint", { url: target })
    },
    [bridge, router, isApp]
  )

  return useMemo(() => ({ hint, navigate }), [hint, navigate])
}
